/**
 * Canvas guide (sections/canvas-guide.liquid)
 *
 * - Photo / side-view toggle on the format cards.
 * - Wall preview: sizes and format drawn to scale above an 84" sofa.
 * - On a product page the cards and the wall preview are tied to the
 *   variant picker (sht-variant-radios): choosing a format or size in the
 *   guide selects it in the picker, and picker changes update the guide.
 * - Smooth scrolling for [data-cg-scroll] links; links whose target is not
 *   on the page are hidden.
 */
(function () {
  'use strict';

  const SOFA_IN = 84;

  // Option names and values in the shopper's language (shared block in layout/theme.liquid), English fallback
  const GR_OPT = (() => {
    const d = { options: { size: 'Size', canvas_type: 'Canvas Type', frame_color: 'Frame Color' },
                values: { stretched_canvas: 'Stretched Canvas', framed_canvas: 'Framed Canvas', float_frame: 'Float Frame',
                          black: 'Black', brown: 'Brown', natural: 'Natural', white: 'White', no_frame: 'No Frame' } };
    try { const el = document.querySelector('[data-gr-i18n="options"]'); const j = el && JSON.parse(el.textContent);
          return j ? { options: Object.assign(d.options, j.options), values: Object.assign(d.values, j.values) } : d; }
    catch (e) { return d; }
  })();

  // UI strings (sections/canvas-guide.liquid), English fallback. Shopify HTML-escapes translations that are not
  // _html keys, so entities such as &#39; are decoded before the text is used.
  const T = (() => {
    const d = {
      hint_small: 'Best on a smaller wall, or hung as a pair above a sofa.',
      hint_medium: 'Balanced over a two-seat sofa, a sideboard or a bed.',
      hint_large: 'Makes a statement on a large wall or above a three-seat sofa.',
      shown: '{size} {format} · to scale',
      shown_with_frame: '{size} {format}, {frame} · to scale',
      frame_black: 'black frame',
      frame_brown: 'brown frame',
      frame_natural: 'natural frame',
      frame_white: 'white frame',
      selected: 'Selected: {size} {format} · {price}',
      selected_with_colour: 'Selected: {size} {format}, {colour} · {price}'
    };
    try {
      const el = document.querySelector('[data-gr-i18n="canvas-guide"]');
      const j = el ? JSON.parse(el.textContent) : {};
      const box = document.createElement('textarea');
      Object.keys(j).forEach((k) => {
        if (typeof j[k] === 'string' && j[k]) {
          box.innerHTML = j[k];
          d[k] = box.value;
        }
      });
    } catch (e) { /* keep the English */ }
    return d;
  })();

  const OPT = { size: GR_OPT.options.size, format: GR_OPT.options.canvas_type, colour: GR_OPT.options.frame_color };
  const FRAME_HEX = { Black: '#1a1a1a', Brown: '#5D4037', White: '#f4f2ee', Natural: '#c8a97e' };
  const DEFAULT_COLOUR = { 'Framed Canvas': 'Black', 'Float Frame': 'Natural' };
  const COLOURS = { 'Framed Canvas': ['Black', 'Brown', 'White'], 'Float Frame': ['Black', 'Natural', 'White'] };
  const SHORT = { 'Stretched Canvas': 'stretched', 'Framed Canvas': 'framed', 'Float Frame': 'float' };

  // The tables above are keyed by the canonical (English) names. Picker and variant values, and the
  // data-cg-finish / data-cg-pick attributes, are in the shopper's language: canon() maps them to the
  // canonical name, local() maps a canonical name back.
  const V = GR_OPT.values;
  const CANON = {
    [V.stretched_canvas]: 'Stretched Canvas', [V.framed_canvas]: 'Framed Canvas', [V.float_frame]: 'Float Frame',
    [V.black]: 'Black', [V.brown]: 'Brown', [V.natural]: 'Natural', [V.white]: 'White', [V.no_frame]: 'No Frame'
  };
  const LOCAL = {};
  Object.keys(CANON).forEach((value) => { LOCAL[CANON[value]] = value; });
  const canon = (value) => (Object.prototype.hasOwnProperty.call(CANON, value) ? CANON[value] : value);
  const local = (name) => (Object.prototype.hasOwnProperty.call(LOCAL, name) ? LOCAL[name] : name);

  // fmt('{size} {format}', { size, format }): fills {name} placeholders
  function fmt(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
  }

  function parseSize(value) {
    const m = String(value).match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i);
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }

  function pretty(value) {
    const d = parseSize(value);
    return d ? `${d.w}×${d.h}″` : value;
  }

  function cm(value) {
    const d = parseSize(value);
    return d ? `${Math.round(d.w * 2.54)}×${Math.round(d.h * 2.54)} cm` : '';
  }

  function scrollToTarget(el) {
    el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  class CanvasGuide {
    constructor(root) {
      this.root = root;
      const dataEl = root.querySelector('[data-cg-data]');
      this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] };
      this.variants = this.data.variants || [];
      this.wall = root.querySelector('[data-cg-wall]');
      this.state = { size: null, format: local('Framed Canvas'), colour: local('Black') };
      this.sizeKey = '';

      this.bindCards();
      if (this.data.onProduct) {
        this.readPicker();
        document.addEventListener('change', (event) => {
          if (event.target.matches && event.target.matches('.js-variant-radio-item') && this.picker() && this.picker().contains(event.target)) {
            // the smart selector may adjust colour and size right after; read once things settle
            clearTimeout(this.readTimer);
            this.readTimer = setTimeout(() => { this.readPicker(); this.render(); }, 60);
          }
        });
      }
      if (this.wall) this.bindWall();
      this.render();
    }

    /* ---------- picker (product pages only) ---------- */
    picker() {
      if (!this.data.onProduct) return null;
      return document.querySelector(`sht-variant-radios[data-product-id="${this.data.productId}"]`);
    }

    pickerValue(name) {
      const picker = this.picker();
      if (!picker) return null;
      const checked = Array.from(picker.querySelectorAll('.js-variant-radio-item')).find((i) => i.name === name && i.checked);
      return checked ? checked.value : null;
    }

    readPicker() {
      this.state.size = this.pickerValue(OPT.size) || this.state.size;
      this.state.format = this.pickerValue(OPT.format) || this.state.format;
      const colour = this.pickerValue(OPT.colour);
      if (colour && colour !== V.no_frame) this.state.colour = colour;
    }

    selectInPicker(name, value) {
      const picker = this.picker();
      if (!picker) return false;
      const input = Array.from(picker.querySelectorAll('.js-variant-radio-item')).find((i) => i.name === name && i.value === value);
      if (!input) return false;
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    /* ---------- format cards ---------- */
    bindCards() {
      this.root.querySelectorAll('.cg-card__media').forEach((media) => {
        media.querySelectorAll('[data-cg-view]').forEach((btn) => {
          btn.addEventListener('click', () => {
            media.dataset.view = btn.dataset.cgView;
            media.querySelectorAll('[data-cg-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          });
        });
      });
      this.root.querySelectorAll('[data-cg-pick]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const format = btn.dataset.cgPick;
          if (this.selectInPicker(OPT.format, format)) {
            this.state.format = format;
            this.render();
            const target = document.getElementById(`variantPicker-${this.data.productId}`) || this.picker();
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    }

    /* ---------- wall preview ---------- */
    bindWall() {
      this.wall.querySelectorAll('[data-cg-finish]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const format = btn.dataset.cgFinish;
          if (this.data.onProduct && this.selectInPicker(OPT.format, format)) return; // picker change re-renders
          this.state.format = format;
          this.render();
        });
      });
    }

    sizesFor(format) {
      const seen = new Set();
      return this.variants
        .filter((v) => v.f === format && !seen.has(v.s) && seen.add(v.s))
        .map((v) => v.s)
        .filter((s) => parseSize(s))
        .sort((a, b) => {
          const A = parseSize(a), B = parseSize(b);
          return A.w * A.h - B.w * B.h || A.w - B.w;
        });
    }

    renderSizes(sizes) {
      const box = this.wall.querySelector('[data-cg-sizes]');
      const key = sizes.join('|');
      if (key !== this.sizeKey) {
        this.sizeKey = key;
        box.innerHTML = '';
        sizes.forEach((s) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cg-size';
          btn.dataset.size = s;
          btn.innerHTML = `<b></b><small></small>`;
          btn.querySelector('b').textContent = pretty(s);
          btn.querySelector('small').textContent = cm(s);
          btn.addEventListener('click', () => {
            if (this.data.onProduct && this.selectInPicker(OPT.size, s)) return;
            this.state.size = s;
            this.render();
          });
          box.appendChild(btn);
        });
      }
      box.querySelectorAll('.cg-size').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.size === this.state.size)));
    }

    render() {
      const format = this.state.format;

      // cards: mark the format chosen in the picker
      if (this.data.onProduct) {
        this.root.querySelectorAll('[data-cg-card]').forEach((card) => {
          const current = card.dataset.cgCard === format;
          card.classList.toggle('is-current', current);
          const btn = card.querySelector('[data-cg-pick]');
          if (btn) {
            btn.textContent = current ? btn.dataset.labelCurrent : btn.dataset.label;
            btn.setAttribute('aria-pressed', String(current));
          }
        });
      }

      if (!this.wall) return;
      const sizes = this.sizesFor(format);
      if (!sizes.length) return;
      if (!sizes.includes(this.state.size)) {
        // closest size by area to the previous one, else a mid size
        const prev = parseSize(this.state.size || '24×30');
        const area = prev ? prev.w * prev.h : 720;
        this.state.size = sizes.reduce((best, s) => {
          const d = parseSize(s), b = parseSize(best);
          return Math.abs(d.w * d.h - area) < Math.abs(b.w * b.h - area) ? s : best;
        }, sizes[0]);
      }
      this.renderSizes(sizes);

      this.wall.querySelectorAll('[data-cg-finish]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cgFinish === format)));

      const dims = parseSize(this.state.size);
      const name = canon(format);
      const kind = SHORT[name] || 'stretched';
      const allowed = COLOURS[name] || [];
      const colour = kind === 'stretched' ? null : (allowed.includes(canon(this.state.colour)) ? this.state.colour : local(DEFAULT_COLOUR[name]));
      const art = this.wall.querySelector('[data-cg-art]');
      art.style.setProperty('--w', dims.w);
      art.style.setProperty('--h', dims.h);
      art.classList.toggle('is-framed', kind === 'framed');
      art.classList.toggle('is-float', kind === 'float');
      if (colour) art.style.setProperty('--frame', FRAME_HEX[canon(colour)]);

      const shown = this.wall.querySelector('[data-cg-shown]');
      const size = pretty(this.state.size);
      shown.textContent = colour
        ? fmt(T.shown_with_frame, { size, format, frame: T[`frame_${String(canon(colour)).toLowerCase()}`] || `${colour.toLowerCase()} frame` })
        : fmt(T.shown, { size, format });

      const pct = Math.round((dims.w / SOFA_IN) * 100);
      this.wall.querySelector('[data-cg-pct]').textContent = `${pct}%`;
      this.wall.querySelector('[data-cg-hint]').textContent =
        dims.w <= 20 ? T.hint_small
          : dims.w <= 30 ? T.hint_medium
            : T.hint_large;

      const sel = this.wall.querySelector('[data-cg-selected]');
      if (sel) {
        const match = this.variants.find((v) => v.s === this.state.size && v.f === format && (kind === 'stretched' || v.c === colour));
        sel.textContent = match ? fmt(colour ? T.selected_with_colour : T.selected, { size: pretty(match.s), format, colour, price: match.p }) : '';
      }
    }
  }

  function initScrollLinks() {
    document.querySelectorAll('[data-cg-scroll]').forEach((link) => {
      const target = document.querySelector(link.dataset.cgScroll);
      if (!target) {
        link.hidden = true;
        return;
      }
      if (link.dataset.cgBound) return;
      link.dataset.cgBound = '1';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        scrollToTarget(target);
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-cg-root]').forEach((root) => {
      if (root.cgGuide) return;
      root.cgGuide = new CanvasGuide(root);
    });
    initScrollLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
